'use client'

import { useState, useEffect } from 'react'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Alert, AlertDescription } from '@creanizer/ui'
import { Slider, Checkbox } from '@creanizer/ui'
import { ChevronDown, ChevronUp, Plus, Trash2, RefreshCw, Edit3, Save, X, Loader2, Wand2, Settings, Globe, Map } from 'lucide-react'
import type { SavedPlot, PlotChapter, PlotCharacter } from '@creanizer/types'
import { usePlotStore } from '../../../../stores/plotStore'
import { useSettingsStore } from '../../../../stores/settingsStore'
import { useWorldBuilderStore } from '../../../../stores/worldbuilderStore'
import { useCharacterStore } from '../../../../stores/characterStore'
import { useEnemyStore } from '../../../../stores/enemyStore'
import { useItemStore } from '../../../../stores/itemStore'
import { useDungeonStore } from '../../../../stores/dungeonStore'
import { EntitySelector } from './EntitySelector'
import { getCurrentGeneratorModel } from '../../../../lib/generatorModelSelection'

// JSONクリーニング関数（共通関数と同じロジック）
function cleanJsonResponse(text: string): string {
  let cleanedJson = text
  
  // コードブロック形式の除去
  if (cleanedJson.includes('```')) {
    const codeBlockMatch = cleanedJson.match(/```(?:json)?\s*([\s\S]*?)```/)
    if (codeBlockMatch) {
      cleanedJson = codeBlockMatch[1]
    }
  }
  
  // 不要な文字を削除
  cleanedJson = cleanedJson
    .replace(/^[^{\[]*/, '') // 先頭の非JSON文字を削除
    .replace(/[^}\]]*$/, '') // 末尾の非JSON文字を削除
    .replace(/,(\s*[}\]])/g, '$1') // 末尾のカンマを削除
    .replace(/([^\\])\\([^\\nrt"'\/bfux])/g, '$1\\\\$2') // 不正なエスケープシーケンスを修正
  
  return cleanedJson.trim()
}

function parseJsonSafely<T>(text: string): T {
  if (!text || text.trim() === '') {
    throw new Error('空のレスポンスです')
  }
  
  const cleanedJson = cleanJsonResponse(text)
  
  try {
    return JSON.parse(cleanedJson)
  } catch (error) {
    console.error('JSON parse error:', error)
    console.error('Cleaned JSON:', cleanedJson.substring(0, 500))
    throw new Error(`レスポンスの解析に失敗しました: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

interface PlotEditorProps {
  plot: SavedPlot
  onClose?: () => void
}

export function PlotEditor({ plot: initialPlot, onClose }: PlotEditorProps) {
  const [plot, setPlot] = useState<SavedPlot>(initialPlot)
  const [expandedChapters, setExpandedChapters] = useState<number[]>([])
  const [editingChapter, setEditingChapter] = useState<number | null>(null)
  const [editingField, setEditingField] = useState<string | null>(null)
  const [tempValue, setTempValue] = useState<any>(null)
  const [regeneratingChapter, setRegeneratingChapter] = useState<number | null>(null)
  const [regeneratingFrom, setRegeneratingFrom] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  
  // 再生成設定
  const [creativityLevel, setCreativityLevel] = useState(5)
  const [useProMode, setUseProMode] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  
  // 世界観・キャラクター等の連携
  const [selectedWorldId, setSelectedWorldId] = useState<string | null>(null)
  const [selectedDungeonId, setSelectedDungeonId] = useState<string | null>(null)
  const [selectedCharacterIds, setSelectedCharacterIds] = useState<string[]>([])
  const [selectedEnemyIds, setSelectedEnemyIds] = useState<string[]>([])
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([])
  
  const { updatePlot, updateChapter, addChapter, deleteChapter, updatePlotField, getPlot, savedPlots } = usePlotStore()
  const { activeApiKey } = useSettingsStore()
  const { savedWorlds } = useWorldBuilderStore()
  const { dungeons } = useDungeonStore()
  const { characters } = useCharacterStore()
  const { enemies } = useEnemyStore()
  const { items } = useItemStore()
  
  // コンポーネントのマウント/アンマウントを追跡
  useEffect(() => {
    console.log('[PlotEditor] Component mounted with plot:', initialPlot.id, initialPlot.title)
    return () => {
      console.log('[PlotEditor] Component unmounting for plot:', initialPlot.id)
    }
  }, [])

  // プロットの変更を監視
  useEffect(() => {
    console.log('[PlotEditor] Plot change detected')
    // ストアから最新のプロットを取得
    const latestPlot = getPlot(initialPlot.id)
    if (latestPlot) {
      setPlot(latestPlot)
    } else {
      setPlot(initialPlot)
    }
  }, [initialPlot, getPlot, savedPlots])

  // 章の展開/折りたたみ
  const toggleChapter = (number: number) => {
    setExpandedChapters(prev =>
      prev.includes(number) 
        ? prev.filter(n => n !== number)
        : [...prev, number]
    )
  }

  // フィールドの編集開始
  const startEditingField = (field: string, value: any) => {
    setEditingField(field)
    setTempValue(value)
  }

  // フィールドの編集保存
  const saveField = () => {
    if (editingField && tempValue !== null) {
      if (editingField === 'characters') {
        updatePlotField(plot.id, 'characters', tempValue)
      } else if (editingField === 'themes') {
        updatePlotField(plot.id, 'themes', tempValue.split('、').map((t: string) => t.trim()))
      } else {
        updatePlotField(plot.id, editingField as keyof typeof plot.result, tempValue)
      }
      setEditingField(null)
      setTempValue(null)
    }
  }

  // 章の編集開始
  const startEditingChapter = (chapter: PlotChapter) => {
    setEditingChapter(chapter.number)
    setTempValue(chapter)
  }

  // 章の編集保存
  const saveChapter = () => {
    if (editingChapter && tempValue) {
      updateChapter(plot.id, editingChapter, tempValue)
      setEditingChapter(null)
      setTempValue(null)
    }
  }

  // 特定の章を再生成
  const regenerateChapter = async (chapterNumber: number) => {
    setRegeneratingChapter(chapterNumber)
    setError(null)
    try {
      const apiKey = activeApiKey?.key
      if (!apiKey) {
        throw new Error('APIキーが設定されていません')
      }

      // コンテキストの構築
      const characterContexts = selectedCharacterIds.map(id => {
        const char = characters.find(c => c.id === id)
        if (!char) return null
        return {
          id,
          name: char.result.name,
          role: 'supporting' as const,
          data: JSON.stringify(char.result)
        }
      }).filter(Boolean)

      const enemyContexts = selectedEnemyIds.map(id => {
        const enemy = enemies.find(e => e.id === id)
        if (!enemy) return null
        return {
          id,
          name: enemy.result.basicInfo.name,
          role: 'threat' as const,
          data: JSON.stringify(enemy.result)
        }
      }).filter(Boolean)

      const itemContexts = selectedItemIds.map(id => {
        const item = items.find(i => i.id === id)
        if (!item) return null
        return {
          id,
          name: item.result.basicInfo.name,
          usage: 'mentioned' as const,
          data: JSON.stringify(item.result)
        }
      }).filter(Boolean)

      // 世界観のコンテキスト
      let worldContext = ''
      if (selectedWorldId) {
        const selectedWorld = savedWorlds.find(w => w.id === selectedWorldId)
        if (selectedWorld) {
          worldContext = `【世界観：${selectedWorld.result.name}】\n${JSON.stringify(selectedWorld.result)}`
        }
      }
      
      // ダンジョンのコンテキスト
      let dungeonContext = ''
      if (selectedDungeonId) {
        const selectedDungeon = dungeons.find(d => d.id === selectedDungeonId)
        if (selectedDungeon) {
          dungeonContext = `【ダンジョン：${selectedDungeon.result.basicInfo.name}】\n${JSON.stringify(selectedDungeon.result)}`
        }
      }

      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/generate/plot/regenerate-chapters`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': apiKey,
        },
        body: JSON.stringify({
          ...plot.input,
          regenerateChapter: chapterNumber,
          existingPlot: plot.result,
          creativityLevel,
          useProMode,
          modelId: getCurrentGeneratorModel('plot'),
          worldContext: worldContext || undefined,
          dungeonContext: dungeonContext || undefined,
          characterContexts: characterContexts.length > 0 ? characterContexts : undefined,
          enemyContexts: enemyContexts.length > 0 ? enemyContexts : undefined,
          itemContexts: itemContexts.length > 0 ? itemContexts : undefined
        })
      })

      const responseText = await response.text()
      console.log('Regenerate chapter response:', responseText)
      
      if (!response.ok) {
        let errorMessage = '章の再生成に失敗しました'
        try {
          const errorData = parseJsonSafely<{ error?: string; message?: string }>(responseText)
          errorMessage = errorData.message || errorData.error || errorMessage
        } catch {
          // JSONとして解析できない場合はそのままテキストを使用
          if (responseText) errorMessage = responseText
        }
        throw new Error(errorMessage)
      }
      
      const data = parseJsonSafely<{ type: string; result: { structure: PlotChapter[] }; timestamp: string }>(responseText)
      
      if (data.result?.structure && data.result.structure[0]) {
        updateChapter(plot.id, chapterNumber, data.result.structure[0])
        setError(null) // 成功時はエラーをクリア
      } else {
        throw new Error('章データが見つかりません')
      }
    } catch (error) {
      console.error('章の再生成に失敗しました:', error)
      setError(error instanceof Error ? error.message : '章の再生成に失敗しました')
    } finally {
      setRegeneratingChapter(null)
    }
  }

  // 特定の章以降を再生成
  const regenerateFromChapter = async (fromNumber: number) => {
    setRegeneratingFrom(fromNumber)
    setError(null)
    try {
      const apiKey = activeApiKey?.key
      if (!apiKey) {
        throw new Error('APIキーが設定されていません')
      }

      // コンテキストの構築（regenerateChapterと同じ）
      const characterContexts = selectedCharacterIds.map(id => {
        const char = characters.find(c => c.id === id)
        if (!char) return null
        return {
          id,
          name: char.result.name,
          role: 'supporting' as const,
          data: JSON.stringify(char.result)
        }
      }).filter(Boolean)

      const enemyContexts = selectedEnemyIds.map(id => {
        const enemy = enemies.find(e => e.id === id)
        if (!enemy) return null
        return {
          id,
          name: enemy.result.basicInfo.name,
          role: 'threat' as const,
          data: JSON.stringify(enemy.result)
        }
      }).filter(Boolean)

      const itemContexts = selectedItemIds.map(id => {
        const item = items.find(i => i.id === id)
        if (!item) return null
        return {
          id,
          name: item.result.basicInfo.name,
          usage: 'mentioned' as const,
          data: JSON.stringify(item.result)
        }
      }).filter(Boolean)

      let worldContext = ''
      if (selectedWorldId) {
        const selectedWorld = savedWorlds.find(w => w.id === selectedWorldId)
        if (selectedWorld) {
          worldContext = `【世界観：${selectedWorld.result.name}】\n${JSON.stringify(selectedWorld.result)}`
        }
      }
      
      // ダンジョンのコンテキスト
      let dungeonContext = ''
      if (selectedDungeonId) {
        const selectedDungeon = dungeons.find(d => d.id === selectedDungeonId)
        if (selectedDungeon) {
          dungeonContext = `【ダンジョン：${selectedDungeon.result.basicInfo.name}】\n${JSON.stringify(selectedDungeon.result)}`
        }
      }

      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/generate/plot/regenerate-chapters`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': apiKey,
        },
        body: JSON.stringify({
          ...plot.input,
          regenerateFromChapter: fromNumber,
          existingPlot: plot.result,
          creativityLevel,
          useProMode,
          modelId: getCurrentGeneratorModel('plot'),
          worldContext: worldContext || undefined,
          dungeonContext: dungeonContext || undefined,
          characterContexts: characterContexts.length > 0 ? characterContexts : undefined,
          enemyContexts: enemyContexts.length > 0 ? enemyContexts : undefined,
          itemContexts: itemContexts.length > 0 ? itemContexts : undefined
        })
      })

      const responseText = await response.text()
      console.log('Regenerate from chapter response:', responseText)
      
      if (!response.ok) {
        let errorMessage = '章の再生成に失敗しました'
        try {
          const errorData = parseJsonSafely<{ error?: string; message?: string }>(responseText)
          errorMessage = errorData.message || errorData.error || errorMessage
        } catch {
          // JSONとして解析できない場合はそのままテキストを使用
          if (responseText) errorMessage = responseText
        }
        throw new Error(errorMessage)
      }
      
      const data = parseJsonSafely<{ type: string; result: { structure: PlotChapter[] }; timestamp: string }>(responseText)
      
      if (data.result?.structure) {
        // 既存の章を更新
        data.result.structure.forEach((chapter: PlotChapter) => {
          if (chapter.number >= fromNumber) {
            updateChapter(plot.id, chapter.number, chapter)
          }
        })
        setError(null) // 成功時はエラーをクリア
      } else {
        throw new Error('章データが見つかりません')
      }
    } catch (error) {
      console.error('章の再生成に失敗しました:', error)
      setError(error instanceof Error ? error.message : '章の再生成に失敗しました')
    } finally {
      setRegeneratingFrom(null)
    }
  }

  // 新しい章を追加
  const handleAddChapter = (afterNumber: number) => {
    console.log('[PlotEditor] Adding chapter after:', afterNumber, 'for plot:', plot.id)
    const newChapter: PlotChapter = {
      number: afterNumber + 1,
      title: '新しい章',
      summary: 'ここに章の内容を記述してください',
      type: 'development'
    }
    addChapter(plot.id, afterNumber, newChapter)
    console.log('[PlotEditor] Chapter added')
  }

  // 章を削除
  const handleDeleteChapter = (chapterNumber: number) => {
    if (confirm(`第${chapterNumber}章を削除しますか？`)) {
      console.log('[PlotEditor] Deleting chapter:', chapterNumber, 'for plot:', plot.id)
      deleteChapter(plot.id, chapterNumber)
      console.log('[PlotEditor] Chapter deleted')
    }
  }

  return (
    <div className="space-y-6">
      {/* 再生成設定 */}
      <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Settings className="h-4 w-4" />
              再生成設定
            </h3>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setShowSettings(!showSettings)}
            >
              {showSettings ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
          
          {showSettings && (
            <div className="space-y-4">
              {/* 創造性レベル */}
              <div className="space-y-2">
                <Label htmlFor="creativity">
                  創造性レベル: {creativityLevel}
                </Label>
                <Slider
                  id="creativity"
                  min={1}
                  max={10}
                  step={1}
                  value={[creativityLevel]}
                  onValueChange={(value) => setCreativityLevel(value[0])}
                />
                <p className="text-xs text-muted-foreground">
                  値が高いほど独創的で予想外の展開になります
                </p>
              </div>

              {/* Proモード */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="useProMode"
                  checked={useProMode}
                  onCheckedChange={(checked) => setUseProMode(checked as boolean)}
                />
                <Label htmlFor="useProMode" className="cursor-pointer">
                  Proモード（高精度・低速）
                </Label>
              </div>

              {/* 世界観の選択 */}
              <EntitySelector
                title="保存された世界観を使用"
                icon="globe"
                entities={savedWorlds.map(world => ({
                  id: world.id,
                  name: world.result.name,
                  summary: world.result.summary
                }))}
                selectedIds={selectedWorldId ? [selectedWorldId] : []}
                onToggle={(id) => setSelectedWorldId(selectedWorldId === id ? null : id)}
              />

              {/* ダンジョンの選択 */}
              <EntitySelector
                title="保存されたダンジョンを使用"
                icon="map"
                entities={dungeons.map(dungeon => ({
                  id: dungeon.id,
                  name: dungeon.result.basicInfo.name,
                  summary: dungeon.result.basicInfo.description,
                  type: `${dungeon.result.structure.floors.length}階層`
                }))}
                selectedIds={selectedDungeonId ? [selectedDungeonId] : []}
                onToggle={(id) => setSelectedDungeonId(selectedDungeonId === id ? null : id)}
              />

              {/* キャラクターの選択 */}
              <EntitySelector
                title="保存されたキャラクターを使用"
                icon="users"
                entities={characters.map(char => ({
                  id: char.id,
                  name: char.result.name,
                  summary: char.result.summary,
                  type: char.result.role
                }))}
                selectedIds={selectedCharacterIds}
                onToggle={(id) => {
                  setSelectedCharacterIds(prev =>
                    prev.includes(id)
                      ? prev.filter(cid => cid !== id)
                      : [...prev, id]
                  )
                }}
              />

              {/* エネミーの選択 */}
              <EntitySelector
                title="保存されたエネミーを使用"
                icon="shield"
                entities={enemies.map(enemy => ({
                  id: enemy.id,
                  name: enemy.result.basicInfo.name,
                  summary: enemy.result.basicInfo.summary,
                  type: enemy.result.basicInfo.threat
                }))}
                selectedIds={selectedEnemyIds}
                onToggle={(id) => {
                  setSelectedEnemyIds(prev =>
                    prev.includes(id)
                      ? prev.filter(eid => eid !== id)
                      : [...prev, id]
                  )
                }}
              />

              {/* アイテムの選択 */}
              <EntitySelector
                title="保存されたアイテムを使用"
                icon="package"
                entities={items.map(item => ({
                  id: item.id,
                  name: item.result.basicInfo.name,
                  summary: item.result.basicInfo.summary,
                  type: item.result.basicInfo.rarity
                }))}
                selectedIds={selectedItemIds}
                onToggle={(id) => {
                  setSelectedItemIds(prev =>
                    prev.includes(id)
                      ? prev.filter(iid => iid !== id)
                      : [...prev, id]
                  )
                }}
              />
            </div>
          )}
      </div>

      {/* 基本情報の編集 */}
      <div className="card">
          <div className="space-y-4">
            {/* タイトル */}
            <div>
              <Label>タイトル</Label>
              {editingField === 'title' ? (
                <div className="flex gap-2">
                  <Input
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    className="flex-1"
                  />
                  <Button type="button" size="sm" onClick={saveField}>
                    <Save className="h-4 w-4" />
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => setEditingField(null)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div 
                  className="p-2 hover:bg-gray-50 rounded cursor-pointer flex items-center justify-between"
                  onClick={() => startEditingField('title', plot.result.title)}
                >
                  <span className="font-medium">{plot.result.title}</span>
                  <Edit3 className="h-4 w-4 text-gray-400" />
                </div>
              )}
            </div>

            {/* 一行あらすじ */}
            <div>
              <Label>一行あらすじ</Label>
              {editingField === 'premise' ? (
                <div className="flex gap-2">
                  <Input
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    className="flex-1"
                  />
                  <Button type="button" size="sm" onClick={saveField}>
                    <Save className="h-4 w-4" />
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => setEditingField(null)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div 
                  className="p-2 hover:bg-gray-50 rounded cursor-pointer flex items-center justify-between"
                  onClick={() => startEditingField('premise', plot.result.premise)}
                >
                  <span className="text-sm">{plot.result.premise}</span>
                  <Edit3 className="h-4 w-4 text-gray-400" />
                </div>
              )}
            </div>

            {/* 詳細あらすじ */}
            <div>
              <Label>詳細あらすじ</Label>
              {editingField === 'synopsis' ? (
                <div className="space-y-2">
                  <Textarea
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    rows={6}
                  />
                  <div className="flex gap-2 justify-end">
                    <Button type="button" size="sm" onClick={saveField}>
                      <Save className="h-4 w-4 mr-1" />
                      保存
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => setEditingField(null)}>
                      キャンセル
                    </Button>
                  </div>
                </div>
              ) : (
                <div 
                  className="p-3 hover:bg-gray-50 rounded cursor-pointer"
                  onClick={() => startEditingField('synopsis', plot.result.synopsis)}
                >
                  <p className="text-sm whitespace-pre-wrap">{plot.result.synopsis}</p>
                  <Edit3 className="h-4 w-4 text-gray-400 mt-2" />
                </div>
              )}
            </div>
          </div>
      </div>

      {/* 章構成の編集 */}
      <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">章構成</h3>
            <Button 
              type="button"
              size="sm" 
              variant="outline"
              onClick={() => handleAddChapter(plot.result.structure.length)}
            >
              <Plus className="h-4 w-4 mr-1" />
              章を追加
            </Button>
          </div>

          <div className="space-y-2">
            {plot.result.structure.map((chapter) => (
              <div key={chapter.number} className="border rounded-lg">
                <div 
                  className="p-3 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                  onClick={() => toggleChapter(chapter.number)}
                >
                  <div className="flex items-center gap-2">
                    {expandedChapters.includes(chapter.number) ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                    <span className="font-medium">
                      第{chapter.number}章: {chapter.title}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation()
                        regenerateChapter(chapter.number)
                      }}
                      disabled={regeneratingChapter === chapter.number}
                    >
                      {regeneratingChapter === chapter.number ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation()
                        regenerateFromChapter(chapter.number)
                      }}
                      disabled={regeneratingFrom === chapter.number}
                      title="この章以降を再生成"
                    >
                      {regeneratingFrom === chapter.number ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Wand2 className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDeleteChapter(chapter.number)
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {expandedChapters.includes(chapter.number) && (
                  <div className="p-4 border-t bg-gray-50">
                    {editingChapter === chapter.number ? (
                      <div className="space-y-3">
                        <div>
                          <Label>章タイトル</Label>
                          <Input
                            value={tempValue?.title || ''}
                            onChange={(e) => setTempValue({ ...tempValue, title: e.target.value })}
                          />
                        </div>
                        <div>
                          <Label>章の内容</Label>
                          <Textarea
                            value={tempValue?.summary || ''}
                            onChange={(e) => setTempValue({ ...tempValue, summary: e.target.value })}
                            rows={4}
                          />
                        </div>
                        <div className="flex gap-2 justify-end">
                          <Button type="button" size="sm" onClick={saveChapter}>
                            <Save className="h-4 w-4 mr-1" />
                            保存
                          </Button>
                          <Button 
                            type="button"
                            size="sm" 
                            variant="outline" 
                            onClick={() => {
                              setEditingChapter(null)
                              setTempValue(null)
                            }}
                          >
                            キャンセル
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div 
                        className="space-y-2 cursor-pointer"
                        onClick={() => startEditingChapter(chapter)}
                      >
                        <p className="text-sm whitespace-pre-wrap">{chapter.summary}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <Edit3 className="h-3 w-3" />
                          クリックして編集
                        </div>
                      </div>
                    )}
                    
                    <div className="mt-3 flex gap-2">
                      <Button 
                        type="button"
                        size="sm" 
                        variant="outline"
                        onClick={() => handleAddChapter(chapter.number)}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        後に章を挿入
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
      </div>

      {/* エラーメッセージ */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* 保存ボタン */}
      <div className="flex justify-end gap-2">
        <Button type="button" onClick={() => updatePlot(plot.id, plot.result)}>
          <Save className="h-4 w-4 mr-2" />
          変更を保存
        </Button>
        {onClose && (
          <Button type="button" variant="outline" onClick={onClose}>
            閉じる
          </Button>
        )}
      </div>
    </div>
  )
}
